import React from 'react';
import { Bot, User } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import ChatMarkdown from './ChatMarkdown';
import { stripMarkerSyntax } from './markers';
import type { ChatMessage } from './useCADChat';

interface CADChatMessagesProps {
  messages: ChatMessage[];
  guidance: string;
  sampleQuestions: string[];
  onSampleSelect: (question: string) => void;
  endRef: React.RefObject<HTMLDivElement | null>;
}

const CADChatMessages: React.FC<CADChatMessagesProps> = ({
  messages,
  guidance,
  sampleQuestions,
  onSampleSelect,
  endRef,
}) => (
  <div className="min-h-0 flex-1 overflow-auto px-2 py-1">
    {messages.length === 0 && (
      <div className="mt-4 text-center">
        <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">{guidance}</p>
        <div className="mt-2 flex flex-col gap-1">
          {sampleQuestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => onSampleSelect(question)}
              className="revy-cjk rounded-md border bg-card px-2 py-1 text-left text-xs leading-[1.5] transition-colors duration-150 ease-shell hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              {question}
            </button>
          ))}
        </div>
      </div>
    )}

    {messages.map((msg, i) => (
      <div key={i} className="mb-2 flex items-start gap-1">
        <span className="mt-0.5 shrink-0">
          {msg.role === 'user' ? (
            <User className="size-4 text-primary" strokeWidth={1.8} />
          ) : (
            <Bot className="size-4 text-muted-foreground" strokeWidth={1.8} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          {msg.role === 'user' ? (
            <p className="revy-cjk text-[0.8rem] font-medium leading-[1.5]">{msg.content}</p>
          ) : msg.content ? (
            <ChatMarkdown text={stripMarkerSyntax(msg.content)} literalValues={msg.literalValues} />
          ) : (
            // label="" keeps the live region without putting its default
            // "loading" text inside the transcript bubble.
            <Spinner size="sm" label="" className="mt-1 text-muted-foreground" />
          )}
        </div>
      </div>
    ))}
    <div ref={endRef} />
  </div>
);

export default CADChatMessages;
